/**
 * WhatsApp message templates written in the platform and sent to Meta for approval. The same rules
 * check the draft in the browser and in the API, so the member sees Meta's likely objections before
 * submitting. An access-code template (Meta's AUTHENTICATION category) has a text Meta writes in the
 * template's language: the company only chooses the security line and the expiry note.
 */

export const TEMPLATE_LIMITS = {
  name: 512,
  header: 60,
  body: 1024,
  footer: 60,
  buttonText: 25,
  url: 2000,
  buttons: 10,
  urlButtons: 2,
  phoneButtons: 1,
  /** Minutes in an access code's expiry note. */
  otpExpirationMin: 1,
  otpExpirationMax: 90,
} as const;

export type WritableTemplateCategory = "marketing" | "utility" | "authentication";

/**
 * An access-code template: Meta's fixed text with the code ("Seu código de verificação é
 * *123456*."), a copy-code button, and the two choices Meta leaves to the company.
 */
export interface TemplateOtp {
  /** Adds Meta's "for your security, do not share this code" after the code. */
  securityRecommendation: boolean;
  /** Adds Meta's "this code expires in N minutes" as the footer. */
  expirationMinutes?: number | null | undefined;
}

export type TemplateButton =
  | { type: "quick_reply"; text: string }
  | { type: "url"; text: string; url: string; example?: string | undefined }
  | { type: "phone"; text: string; phone: string };

/** A header with a photo, a video or a document instead of text. */
export type TemplateMediaFormat = "image" | "video" | "document";

/** What Meta accepts in a media header, by format (the same file types as media messages). */
export const TEMPLATE_HEADER_MEDIA: Record<
  TemplateMediaFormat,
  { mimes: readonly string[]; maxBytes: number }
> = {
  image: { mimes: ["image/jpeg", "image/png"], maxBytes: 5 * 1024 * 1024 },
  video: { mimes: ["video/mp4", "video/3gpp"], maxBytes: 16 * 1024 * 1024 },
  document: { mimes: ["application/pdf"], maxBytes: 100 * 1024 * 1024 },
};

/** The sample file Meta's reviewers see in a media header, kept with the template. */
export interface TemplateMediaSample {
  storageKey: string;
  mime: string;
  filename?: string | undefined;
  sizeBytes?: number | undefined;
}

export interface TemplateDraft {
  name: string;
  /** Meta's language code: pt_BR, es, es_MX, en_US… */
  language: string;
  category: WritableTemplateCategory;
  header?: { text: string; example?: string | undefined } | null | undefined;
  /**
   * A media header instead of the text one; each send brings its own file, and Meta reviews the
   * template with the sample.
   */
  headerMedia?:
    | { format: TemplateMediaFormat; sample?: TemplateMediaSample | null | undefined }
    | null
    | undefined;
  /** A map pin on top (Meta's LOCATION header); each send brings the place. */
  headerLocation?: boolean | undefined;
  body: string;
  /** One example per {{n}} of the body, in order. Meta requires them to review the template. */
  bodyExamples: string[];
  footer?: string | null | undefined;
  buttons: TemplateButton[];
  /** An access-code template's choices; header, body, footer and buttons are Meta's own. */
  otp?: TemplateOtp | null | undefined;
}

export interface TemplateIssue {
  field: "name" | "header" | "body" | "footer" | "buttons" | "language" | "otp";
  code:
    | "name_format"
    | "name_too_long"
    | "language_required"
    | "header_too_long"
    | "header_format"
    | "header_variables"
    | "header_example_missing"
    | "header_media_sample_missing"
    | "header_media_type"
    | "header_media_too_large"
    | "header_conflict"
    | "body_required"
    | "body_too_long"
    | "variables_not_sequential"
    | "variable_at_edge"
    | "example_missing"
    | "footer_too_long"
    | "footer_variables"
    | "too_many_buttons"
    | "too_many_url_buttons"
    | "too_many_phone_buttons"
    | "buttons_not_grouped"
    | "button_text_required"
    | "button_text_too_long"
    | "button_text_duplicate"
    | "url_invalid"
    | "url_variable_position"
    | "url_example_missing"
    | "phone_invalid"
    | "otp_expiration_range";
  /** Which button, for button issues. */
  index?: number;
}

const VARIABLE = /\{\{\s*(\d+)\s*\}\}/g;

/** The numbers of the {{n}} placeholders, in order of appearance. */
export function templateVariables(text: string): number[] {
  return [...text.matchAll(VARIABLE)].map((m) => Number(m[1]));
}

/** "Lembrete de visita!" → "lembrete_de_visita": the only characters Meta accepts in a name. */
export function templateName(label: string): string {
  return label
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, TEMPLATE_LIMITS.name);
}

function sequential(nums: number[]): boolean {
  const unique = [...new Set(nums)].sort((a, b) => a - b);
  return unique.every((n, i) => n === i + 1);
}

/** Everything Meta would object to before review, in field order. */
export function validateTemplate(d: TemplateDraft): TemplateIssue[] {
  const issues: TemplateIssue[] = [];
  if (!/^[a-z0-9_]+$/.test(d.name)) issues.push({ field: "name", code: "name_format" });
  if (d.name.length > TEMPLATE_LIMITS.name) issues.push({ field: "name", code: "name_too_long" });
  if (!d.language.trim()) issues.push({ field: "language", code: "language_required" });
  // Meta writes an access code's text: only the expiry is the company's to get wrong.
  if (d.category === "authentication") {
    const minutes = d.otp?.expirationMinutes;
    if (
      minutes != null &&
      (!Number.isInteger(minutes) ||
        minutes < TEMPLATE_LIMITS.otpExpirationMin ||
        minutes > TEMPLATE_LIMITS.otpExpirationMax)
    )
      issues.push({ field: "otp", code: "otp_expiration_range" });
    return issues;
  }

  if (d.headerLocation && (d.header?.text || d.headerMedia))
    issues.push({ field: "header", code: "header_conflict" });
  if (d.headerMedia) {
    if (d.header?.text) issues.push({ field: "header", code: "header_conflict" });
    const rules = TEMPLATE_HEADER_MEDIA[d.headerMedia.format];
    const sample = d.headerMedia.sample;
    if (!sample) issues.push({ field: "header", code: "header_media_sample_missing" });
    else if (!rules.mimes.includes(sample.mime))
      issues.push({ field: "header", code: "header_media_type" });
    else if (sample.sizeBytes && sample.sizeBytes > rules.maxBytes)
      issues.push({ field: "header", code: "header_media_too_large" });
  }
  if (d.header?.text) {
    if (d.header.text.length > TEMPLATE_LIMITS.header)
      issues.push({ field: "header", code: "header_too_long" });
    if (/[\r\n]/.test(d.header.text)) issues.push({ field: "header", code: "header_format" });
    const vars = templateVariables(d.header.text);
    if (vars.length > 1 || (vars.length === 1 && vars[0] !== 1))
      issues.push({ field: "header", code: "header_variables" });
    else if (vars.length === 1 && !d.header.example?.trim())
      issues.push({ field: "header", code: "header_example_missing" });
  }

  const body = d.body.trim();
  if (!body) issues.push({ field: "body", code: "body_required" });
  if (d.body.length > TEMPLATE_LIMITS.body) issues.push({ field: "body", code: "body_too_long" });
  const vars = templateVariables(d.body);
  if (vars.length) {
    if (!sequential(vars)) issues.push({ field: "body", code: "variables_not_sequential" });
    // Meta rejects a body that starts or ends with a variable.
    if (/^\{\{\s*\d+\s*\}\}/.test(body) || /\{\{\s*\d+\s*\}\}[.!?]?$/.test(body))
      issues.push({ field: "body", code: "variable_at_edge" });
    const count = new Set(vars).size;
    if (d.bodyExamples.length < count || d.bodyExamples.slice(0, count).some((e) => !e.trim()))
      issues.push({ field: "body", code: "example_missing" });
  }

  if (d.footer) {
    if (d.footer.length > TEMPLATE_LIMITS.footer)
      issues.push({ field: "footer", code: "footer_too_long" });
    if (templateVariables(d.footer).length)
      issues.push({ field: "footer", code: "footer_variables" });
  }

  if (d.buttons.length > TEMPLATE_LIMITS.buttons)
    issues.push({ field: "buttons", code: "too_many_buttons" });
  if (d.buttons.filter((b) => b.type === "url").length > TEMPLATE_LIMITS.urlButtons)
    issues.push({ field: "buttons", code: "too_many_url_buttons" });
  if (d.buttons.filter((b) => b.type === "phone").length > TEMPLATE_LIMITS.phoneButtons)
    issues.push({ field: "buttons", code: "too_many_phone_buttons" });
  // Quick replies and call-to-action buttons may be combined, but each kind stays together.
  const kinds = d.buttons.map((b) => (b.type === "quick_reply" ? "reply" : "action"));
  if (kinds.some((k, i) => i > 1 && k !== kinds[i - 1] && kinds.slice(0, i - 1).includes(k)))
    issues.push({ field: "buttons", code: "buttons_not_grouped" });
  const seen = new Set<string>();
  d.buttons.forEach((b, index) => {
    const text = b.text.trim().toLowerCase();
    if (!text) issues.push({ field: "buttons", code: "button_text_required", index });
    else if (b.text.length > TEMPLATE_LIMITS.buttonText)
      issues.push({ field: "buttons", code: "button_text_too_long", index });
    else if (seen.has(text))
      issues.push({ field: "buttons", code: "button_text_duplicate", index });
    seen.add(text);
    if (b.type === "url") {
      const vars = templateVariables(b.url);
      if (
        !/^https:\/\/[^\s{}]+(\{\{\s*1\s*\}\})?$/.test(b.url.replace(/\{\{\s*1\s*\}\}$/, "{{1}}"))
      )
        issues.push({
          field: "buttons",
          code: vars.length ? "url_variable_position" : "url_invalid",
          index,
        });
      else if (b.url.length > TEMPLATE_LIMITS.url)
        issues.push({ field: "buttons", code: "url_invalid", index });
      else if (vars.length && !b.example?.trim())
        issues.push({ field: "buttons", code: "url_example_missing", index });
    }
    if (b.type === "phone" && !/^\+\d{8,15}$/.test(b.phone.replace(/[\s()-]/g, "")))
      issues.push({ field: "buttons", code: "phone_invalid", index });
  });
  return issues;
}

export interface TemplateWarning {
  field: "header" | "body" | "buttons" | "category";
  code: "variables_dense" | "adjacent_variables" | "promotional_utility" | "url_shortener";
}

const PROMOTIONAL =
  /\b(oferta|desconto|promo[cç][aã]o|promocional|gr[aá]tis|cupom|liquida[cç][aã]o|imperd[ií]vel|aproveite|black friday|oferta|descuento|promoci[oó]n|gratis|cup[oó]n|aprovech[ae]|discount|offer|promo|coupon|free|sale|deal)\b|\d+\s?%/i;
const SHORTENER =
  /\b(bit\.ly|tinyurl\.com|t\.co|goo\.gl|ow\.ly|is\.gd|cutt\.ly|rebrand\.ly|encurtador\.com\.br)\//i;

/**
 * What often makes Meta reject or recategorize a template, without being a hard rule: the member
 * may still send it.
 */
export function templateWarnings(d: TemplateDraft): TemplateWarning[] {
  const warnings: TemplateWarning[] = [];
  if (d.category === "authentication") return warnings;
  const vars = new Set(templateVariables(d.body)).size;
  const fixed = d.body.replace(VARIABLE, "").replace(/\s+/g, " ").trim().length;
  // Meta refuses bodies that are mostly variables; about 20 fixed characters per variable is safe.
  if (vars && fixed / vars < 20) warnings.push({ field: "body", code: "variables_dense" });
  if (/\}\}\s*\{\{/.test(d.body)) warnings.push({ field: "body", code: "adjacent_variables" });
  if (
    d.category === "utility" &&
    PROMOTIONAL.test([d.header?.text ?? "", d.body, d.footer ?? ""].join(" "))
  )
    warnings.push({ field: "category", code: "promotional_utility" });
  if (SHORTENER.test(d.body) || d.buttons.some((b) => b.type === "url" && SHORTENER.test(b.url)))
    warnings.push({ field: "buttons", code: "url_shortener" });
  return warnings;
}

/** An access code's components as Meta creates them: Meta fills in the text. */
function otpComponents(otp: TemplateOtp | null | undefined): Array<Record<string, unknown>> {
  return [
    { type: "BODY", add_security_recommendation: Boolean(otp?.securityRecommendation) },
    ...(otp?.expirationMinutes
      ? [{ type: "FOOTER", code_expiration_minutes: otp.expirationMinutes }]
      : []),
    { type: "BUTTONS", buttons: [{ type: "OTP", otp_type: "COPY_CODE" }] },
  ];
}

/**
 * The draft in the shape POST /{waba}/message_templates expects. A media header carries the
 * handle of its sample, uploaded to Meta when the template is sent for review.
 */
export function toMetaTemplate(
  d: TemplateDraft,
  options: { headerHandle?: string } = {},
): {
  name: string;
  language: string;
  category: "MARKETING" | "UTILITY" | "AUTHENTICATION";
  parameter_format?: "positional";
  components: Array<Record<string, unknown>>;
} {
  if (d.category === "authentication")
    return {
      name: d.name,
      language: d.language,
      category: "AUTHENTICATION",
      components: otpComponents(d.otp),
    };
  const components: Array<Record<string, unknown>> = [];
  if (d.headerLocation) components.push({ type: "HEADER", format: "LOCATION" });
  else if (d.headerMedia)
    components.push({
      type: "HEADER",
      format: d.headerMedia.format.toUpperCase(),
      ...(options.headerHandle ? { example: { header_handle: [options.headerHandle] } } : {}),
    });
  else if (d.header?.text)
    components.push({
      type: "HEADER",
      format: "TEXT",
      text: d.header.text,
      ...(templateVariables(d.header.text).length
        ? { example: { header_text: [d.header.example ?? ""] } }
        : {}),
    });
  const bodyVars = new Set(templateVariables(d.body)).size;
  components.push({
    type: "BODY",
    text: d.body,
    ...(bodyVars ? { example: { body_text: [d.bodyExamples.slice(0, bodyVars)] } } : {}),
  });
  if (d.footer?.trim()) components.push({ type: "FOOTER", text: d.footer });
  if (d.buttons.length)
    components.push({
      type: "BUTTONS",
      buttons: d.buttons.map((b) =>
        b.type === "quick_reply"
          ? { type: "QUICK_REPLY", text: b.text }
          : b.type === "url"
            ? {
                type: "URL",
                text: b.text,
                url: b.url,
                ...(templateVariables(b.url).length ? { example: [b.example ?? ""] } : {}),
              }
            : { type: "PHONE_NUMBER", text: b.text, phone_number: b.phone.replace(/[\s()-]/g, "") },
      ),
    });
  return {
    name: d.name,
    language: d.language,
    category: d.category === "marketing" ? "MARKETING" : "UTILITY",
    parameter_format: "positional",
    components,
  };
}

interface MetaComponent {
  type?: string;
  format?: string;
  text?: string;
  add_security_recommendation?: boolean;
  code_expiration_minutes?: number;
  example?: { header_text?: string[]; body_text?: string[][] };
  buttons?: Array<{
    type?: string;
    otp_type?: string;
    text?: string;
    url?: string;
    phone_number?: string;
    example?: string[];
  }>;
}

/**
 * An access-code template's choices from Meta's components. Meta may list the copy-code button as
 * OTP or as its whatsapp.com/otp link; one-tap and zero-tap buttons are not written here.
 */
function otpDraftFromMeta(
  t: { name: string; language: string },
  components: MetaComponent[],
): TemplateDraft | null {
  const byType = (type: string) => components.find((c) => c.type?.toUpperCase() === type);
  const buttons = byType("BUTTONS")?.buttons ?? [];
  const copyCode = (b: (typeof buttons)[number]) =>
    b.type?.toUpperCase() === "OTP"
      ? (b.otp_type ?? "COPY_CODE").toUpperCase() === "COPY_CODE"
      : b.type?.toUpperCase() === "URL" && /otp_type=COPY_CODE/i.test(b.url ?? "");
  if (buttons.length !== 1 || !copyCode(buttons[0]!)) return null;
  const minutes = byType("FOOTER")?.code_expiration_minutes;
  return {
    name: t.name,
    language: t.language,
    category: "authentication",
    header: null,
    body: "",
    bodyExamples: [],
    footer: null,
    buttons: [],
    otp: {
      securityRecommendation: Boolean(byType("BODY")?.add_security_recommendation),
      expirationMinutes: typeof minutes === "number" ? minutes : null,
    },
  };
}

/**
 * The editable draft of a template that exists only as Meta's components (made in WhatsApp Manager
 * or synced), so it can be fixed or duplicated here. Null when it uses what this editor does not
 * write: one-tap codes, carousels, named parameters, catalog or flow buttons.
 */
export function draftFromMeta(t: {
  name: string;
  language: string;
  category: string;
  components: unknown;
}): TemplateDraft | null {
  const components = (Array.isArray(t.components) ? t.components : []) as MetaComponent[];
  if (t.category === "authentication") return otpDraftFromMeta(t, components);
  if (t.category !== "marketing" && t.category !== "utility") return null;
  const byType = (type: string) => components.find((c) => c.type?.toUpperCase() === type);
  const known = new Set(["HEADER", "BODY", "FOOTER", "BUTTONS"]);
  if (components.some((c) => !known.has(c.type?.toUpperCase() ?? ""))) return null;
  const header = byType("HEADER");
  const headerFormat = (header?.format ?? "TEXT").toUpperCase();
  const mediaFormat = (
    { IMAGE: "image", VIDEO: "video", DOCUMENT: "document" } as Record<string, TemplateMediaFormat>
  )[headerFormat];
  const location = headerFormat === "LOCATION";
  if (header && headerFormat !== "TEXT" && !mediaFormat && !location) return null;
  const body = byType("BODY");
  if (!body?.text) return null;
  const named = /\{\{\s*[a-z_][a-z0-9_]*\s*\}\}/i;
  if (named.test(body.text) || (header?.text && named.test(header.text))) return null;
  const buttons: TemplateButton[] = [];
  for (const b of byType("BUTTONS")?.buttons ?? []) {
    const type = b.type?.toUpperCase();
    if (type === "QUICK_REPLY") buttons.push({ type: "quick_reply", text: b.text ?? "" });
    else if (type === "URL")
      buttons.push({
        type: "url",
        text: b.text ?? "",
        url: b.url ?? "",
        ...(b.example?.[0] ? { example: b.example[0] } : {}),
      });
    else if (type === "PHONE_NUMBER")
      buttons.push({ type: "phone", text: b.text ?? "", phone: b.phone_number ?? "" });
    else return null;
  }
  return {
    name: t.name,
    language: t.language,
    category: t.category,
    ...(location ? { headerLocation: true } : {}),
    header:
      header?.text && !mediaFormat
        ? {
            text: header.text,
            ...(header.example?.header_text?.[0] ? { example: header.example.header_text[0] } : {}),
          }
        : null,
    // Meta keeps the reviewed sample; editing here asks for the file again.
    ...(mediaFormat ? { headerMedia: { format: mediaFormat, sample: null } } : {}),
    body: body.text,
    bodyExamples: body.example?.body_text?.[0] ?? [],
    footer: byType("FOOTER")?.text ?? null,
    buttons,
  };
}

/** The languages offered in the editor, as Meta names them. */
export const TEMPLATE_LANGUAGES = [
  "pt_BR",
  "es",
  "es_AR",
  "es_MX",
  "es_ES",
  "en_US",
  "en_GB",
  "pt_PT",
] as const;
